// src/pages/Register.js — o cadastro (/register): estado, regras e chamadas.
//
// Out/26: a TELA saiu daqui. Dois visuais, do Claude Design, sobre o mesmo
// miolo (cadastro/TelaCadastro.js):
//   - cadastro/CadastroComum.js — Starter e Business ("Cadastro.dc.html"),
//     e o PLUS quando a campanha da Black Friday está desligada;
//   - blackFriday/CadastroPlus.js — o PLUS na campanha.
// Este arquivo ficou com o que não pode divergir entre os dois: estado,
// validações, criação da conta, KYB, checkout do Stripe, retomada, contrato
// (o modal abaixo é o mesmo nos dois) e o Embedded Signup — tudo vai por
// props. Regra de cadastro nova entra aqui, uma vez.
//
// Herdado da v2 (jun/26) e que continua valendo:
//   - 4 etapas; a 4 ("Conectar WhatsApp") tem o EmbeddedSignupButton e o
//     "Conectar depois" → /dashboard.
//   - O Stripe volta pra /register?step=3 (não pra /checkout/success) e o
//     cadastro termina aqui; o ?step some da URL depois de usado. É ali que
//     sai o Pixel da ativação (CompleteRegistration + StartTrial/Purchase).
//   - handleSubmitSession1/2, handleStartCheckout, fetchCNPJ (Receita),
//     modal do contrato com leitura até o fim, token no localStorage desde a
//     etapa 1 (a volta do Stripe e o Embedded Signup precisam dele) e o
//     upload dos documentos KYB.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { trackFbqEvent, getPlanValueBRL, dadosDeAtribuicao } from '../hooks/useMetaPixel';
import {
  Box, Typography, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, LinearProgress
} from '@mui/material';
import CadastroComum from './cadastro/CadastroComum';
import CadastroPlus from './blackFriday/CadastroPlus';
import { CAMPANHA_LIGADA } from './blackFriday/campanha';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.cettosystem.com.br';

// ─── Paleta v2 (wireframe HTML do usuário) ─────────────────────────────────
// Desde out/26 só o modal do contrato (MUI) e a cor de cada plano usam: a
// tela do cadastro tem a própria paleta em cadastro/cadastro.css.
const C = {
  bg:        '#0A0F1A',
  bg2:       '#0E1525',
  bg4:       '#1A2540',
  bdr:       'rgba(255,255,255,0.07)',
  green:     '#00D67C',
  blue:      '#5BA4F5',
  text:      '#DDE4F0',
  text2:     '#7A8799',
  text3:     '#3E4D60',
};
const FONT_TXT = "'Syne', 'Segoe UI', sans-serif";
const FONT_NUM = "'Outfit', 'Segoe UI', sans-serif";

// Templates por dia, por número: o teto do plano é daily_cap_per_device
// (backend/app/plan_limits.py) e vale com o número aquecido — todo número
// novo começa na faixa de 250 da própria Meta, que sobe sozinha. A Cetto só
// informa o 250; quem limita no começo é a Meta (out/26).
const envioDiario = (teto) => ({
  texto: `Até ${teto} templates/dia por número`,
  nota: 'Começa com 250/dia até o número aquecer — regra da Meta',
});

// Cards da etapa 1. Preço espelha STRIPE_PRICE_* e o teto diário espelha
// daily_cap_per_device (plan_limits.py) — até out/26 aqui dizia 100/250 por
// dia e o servidor deixava 500/1.000. Templates NÃO entram na mensalidade: a
// Cetto é Tech Provider da Meta e não fatura template — quem cobra é a Meta,
// no cartão que o cliente cadastra no Embedded Signup. "Números" e não
// "chips", como na oferta da Black Friday.
const PLANS = {
  STARTER: {
    name: 'Starter',
    price: 'R$ 139',
    priceUnit: '/mês',
    color: C.green,
    features: [
      '1 número WhatsApp oficial',
      '1 agente + 1 supervisor',
      'Templates pagos direto à Meta',
      envioDiario('1.000'),
      'Monitoramento 24/7',
    ],
  },
  BUSINESS: {
    name: 'Business',
    price: 'R$ 339',
    priceUnit: '/mês',
    color: C.blue,
    features: [
      '5 números WhatsApp oficiais',
      '5 agentes + 1 supervisor',
      'Templates pagos direto à Meta',
      envioDiario('2.000'),
      'CRM completo + Automação',
      'Suporte prioritário',
    ],
    popular: true,
  },
};

// Planos sob medida (out/26): não entram nos cards públicos. Só aparecem
// para quem chega pelo link direto (/register?plan=plus) — e aí aparecem
// SOZINHOS, sem o "Trocar para", para o cliente não cair num plano
// diferente do que foi combinado. Limites em plan_limits.py.
const PLANOS_SOB_MEDIDA = {
  PLUS: {
    name: 'Plus',
    price: 'R$ 249',
    priceUnit: '/mês',
    color: C.green,
    features: [
      '3 números WhatsApp oficiais',
      '1 agente + 1 supervisor',
      'Templates pagos direto à Meta',
      envioDiario('2.000'),
      'Monitoramento 24/7',
    ],
  },
};

// ============================================================
// CONTRATO DE PRESTAÇÃO DE SERVIÇOS — CETTO SYSTEM
// (Inalterado da v1)
// ============================================================
const CONTRATO_CETTO = `CONTRATO DE PRESTAÇÃO DE SERVIÇOS DE SOFTWARE (SaaS) E GERENCIAMENTO DE INTEGRAÇÕES

Pelo presente instrumento particular, de um lado, a CETTO SYSTEM, doravante denominada "CONTRATADA", e, de outro lado, a pessoa física ou jurídica identificada no ato do cadastro, doravante denominada "CONTRATANTE", celebram o presente Contrato de Prestação de Serviços, que se regerá pelas seguintes cláusulas e condições:

CLÁUSULA 1 - DO OBJETO
1.1. O presente contrato tem por objeto o licenciamento de uso de software, na modalidade "Software as a Service" (SaaS), fornecido pela CONTRATADA à CONTRATANTE, para gestão de atendimento e envio de mensagens via integração com a API Oficial do WhatsApp (WhatsApp Business API), fornecida pela Meta Platforms, Inc. ("Meta").

CLÁUSULA 2 - DAS RESPONSABILIDADES DA CONTRATADA
2.1. A CONTRATADA compromete-se a manter o sistema disponível 24 (vinte e quatro) horas por dia, 7 (sete) dias por semana, ressalvadas as paradas para manutenção programada, atualizações de segurança e falhas decorrentes de serviços de terceiros (como provedores de nuvem e a própria Meta).
2.2. A CONTRATADA realizará o intermédio burocrático junto à Meta para aprovação da conta (WABA) e verificação do negócio (KYB). No entanto, a CONTRATADA não garante a aprovação, visto que esta decisão é de competência exclusiva da Meta.

CLÁUSULA 3 - DAS RESPONSABILIDADES DA CONTRATANTE
3.1. A CONTRATANTE declara que todas as informações fornecidas no cadastro (incluindo Razão Social, CNPJ, Endereço e Representante Legal) são verídicas e atualizadas, assumindo total responsabilidade civil e criminal por eventuais falsidades.
3.2. A CONTRATANTE compromete-se a utilizar a plataforma em estrita observância às Políticas de Comércio e Políticas de Mensagens do WhatsApp. É expressamente proibido o envio de mensagens caracterizadas como SPAM, fraudes, conteúdos ilegais, ofensivos, discriminatórios ou que violem direitos de terceiros.
3.3. A CONTRATANTE é a única e exclusiva responsável pelo conteúdo das mensagens enviadas através da plataforma.
3.4. A CONTRATANTE reconhece que a Meta possui autoridade para banir, suspender ou bloquear números de WhatsApp que violem suas políticas. A CONTRATADA não se responsabiliza por bloqueios impostos pela Meta e não fará reembolso de valores pagos em caso de banimento por mau uso.

CLÁUSULA 4 - DA COBRANÇA E DO FATURAMENTO
4.1. A remuneração pelos serviços será feita de forma recorrente (mensal), de acordo com o plano escolhido no ato da contratação.
4.2. A remuneração prevista na cláusula 4.1 refere-se exclusivamente ao uso da plataforma. Os envios de mensagens ativas ("Templates") são tarifados e cobrados diretamente pela Meta Platforms, Inc., no meio de pagamento que a CONTRATANTE cadastra junto à Meta durante a ativação da conta (Embedded Signup). A CONTRATADA atua como Provedora de Tecnologia (Tech Provider) da Meta, não intermedeia, não revende e não fatura o custo de Templates, e não responde por reajustes de tarifa praticados pela Meta.
4.3. O atraso no pagamento da mensalidade implicará na suspensão automática do acesso à plataforma e, consequentemente, na interrupção do envio e recebimento de mensagens.

CLÁUSULA 5 - DA PRIVACIDADE E PROTEÇÃO DE DADOS (LGPD)
5.1. As partes comprometem-se a cumprir todas as disposições da Lei Geral de Proteção de Dados (Lei nº 13.709/2018).
5.2. A CONTRATADA atuará como Operadora de Dados em relação aos dados pessoais de clientes da CONTRATANTE (titulares dos dados) que transitarem pela plataforma, tratando-os exclusivamente para a finalidade de execução deste contrato.
5.3. A CONTRATADA adota medidas técnicas e administrativas de segurança para proteger os dados contra acessos não autorizados. No entanto, não se responsabiliza por vazamentos decorrentes de negligência da CONTRATANTE no manuseio de senhas ou compartilhamento indevido de acessos.

CLÁUSULA 6 - DO CANCELAMENTO E RESCISÃO
6.1. O presente contrato é celebrado por prazo indeterminado, podendo ser cancelado por qualquer das partes, a qualquer momento, sem incidência de multa rescisória, desde que não haja faturas em aberto.
6.2. O cancelamento por parte da CONTRATANTE deverá ser realizado diretamente no painel administrativo da plataforma ou mediante solicitação formal ao suporte.
6.3. A CONTRATADA poderá rescindir o contrato imediatamente, sem aviso prévio, caso identifique uso fraudulento, ilícito ou violação das políticas da Meta por parte da CONTRATANTE.

CLÁUSULA 7 - DO FORO
7.1. Fica eleito o foro da comarca da sede da CONTRATADA para dirimir quaisquer dúvidas ou controvérsias oriundas do presente contrato, com renúncia expressa a qualquer outro, por mais privilegiado que seja.

Ao clicar em "Li e Aceito", a CONTRATANTE declara ter lido, compreendido e concordado integralmente com todos os termos e condições estabelecidos neste instrumento.`;

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();

  // ── URL params ──────────────────────────────────────────────────────────
  // ?plan=BUSINESS  → preset do plano no Step 1 (?plan=plus → plano sob medida)
  // ?step=3         → vindo do redirect Stripe pós-pagamento, jumpa pro Step 4
  // ?session_id=... → preenchido pelo Stripe (ignoramos por ora, vale só como evidência)
  const params = new URLSearchParams(location.search);
  const planoDaUrl = params.get('plan')?.toUpperCase();
  const preSelectedPlan = (PLANS[planoDaUrl] || PLANOS_SOB_MEDIDA[planoDaUrl]) ? planoDaUrl : 'STARTER';
  const urlStep = parseInt(params.get('step') || '0', 10);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);

  // Sessão 1 (Identidade)
  // Na volta do Stripe (?step=3) a URL não traz o plano: ele vem do
  // pending_checkout gravado antes do checkout — é o que faz o passo 4 do
  // PLUS abrir no visual da Black Friday.
  const [session1, setSession1] = useState(() => {
    let plano = preSelectedPlan;
    if (urlStep === 3 && !planoDaUrl) {
      try {
        const p = JSON.parse(localStorage.getItem('pending_checkout') || 'null')?.plan_type;
        if (PLANS[p] || PLANOS_SOB_MEDIDA[p]) plano = p;
      } catch { /* sem o pending_checkout, fica o padrão */ }
    }
    return { name: '', email: '', company_name: '', password: '', confirmPassword: '', plan_type: plano };
  });

  // Sessão 2 (KYB enxuto — pós refatoração jun/26)
  const [session2, setSession2] = useState({
    razao_social: '', cnpj: '', website: '',
    doc_cartao_cnpj: null, doc_contrato_social: null, doc_comprovante_endereco: null,
    terms_accepted: false,
  });

  // Token JWT da Sessão 1 — persistido em localStorage pra sobreviver ao
  // redirect do Stripe e autenticar o Embedded Signup no Step 4.
  const [tempToken, setTempToken] = useState(() => localStorage.getItem('token') || '');

  // ─── Cadastro retomado (out/26) ────────────────────────────────────────
  // Quem criou a conta e não terminou o pagamento (fechou a página do
  // Stripe, cancelou, cartão recusado) volta por /register?retomar=1: do
  // painel bloqueado, do "Tentar novamente" do pagamento cancelado e do
  // Upgrade de quem ainda não assinou. Antes o cadastro recomeçava do zero e
  // parava em "E-mail já cadastrado", e a pessoa acabava no botão do painel,
  // que abria o pagamento sem os 7 dias grátis. Retomado, o passo 1 (que
  // criaria a conta de novo) fica fechado.
  const retomar = params.get('retomar') === '1';
  const [retomando, setRetomando] = useState(false);
  const [carregandoRetomada, setCarregandoRetomada] = useState(retomar && !!tempToken);
  // Quem já teve assinatura não ganha o teste de novo (o servidor decide;
  // isto só muda o texto do passo de pagamento).
  const [testeDisponivel, setTesteDisponivel] = useState(true);
  // Confirmação do e-mail (out/26): o cadastro manda um código; o passo 2
  // avisa, sem pedir nada agora — a faixa do painel cuida disso.
  const [codigoEnviadoPara, setCodigoEnviadoPara] = useState('');
  // Passo 4: se o checkout entrou com teste (gravado em pending_checkout
  // antes de ir pro Stripe). null = voltou em outro navegador, não se sabe.
  const [voltouComTeste] = useState(() => {
    try {
      const p = JSON.parse(localStorage.getItem('pending_checkout') || 'null');
      return p && typeof p.trial === 'boolean' ? p.trial : null;
    } catch { return null; }
  });

  // Modal do contrato
  const [contratoOpen, setContratoOpen] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const contratoRef = useRef(null);

  // ─── Jump pro Step 4 quando volta do Stripe ────────────────────────────
  // O Stripe redireciona pra /register?step=3&session_id=... Em vez de criar
  // uma rota separada, detectamos o param aqui e pulamos pro step certo.
  useEffect(() => {
    if (urlStep === 3 && tempToken) {
      setActiveStep(3);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retomada: lê a assinatura e abre no passo certo — Dados Empresariais se
  // o CNPJ não foi gravado (o contrato é aceito ali), senão Plano e Pagamento.
  // Já assinou → painel. Token vencido → cadastro do zero.
  useEffect(() => {
    if (!retomar || !tempToken) return;
    let vivo = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/billing/subscription-status`, {
          headers: { Authorization: `Bearer ${tempToken}` }
        });
        if (!vivo) return;
        if (['ACTIVE', 'TRIALING', 'PAST_DUE'].includes(data.subscription_status)) {
          navigate('/dashboard', { replace: true });
          return;
        }
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const plano = [planoDaUrl, data.plan_type, (user.plan || '').toUpperCase()]
          .find((p) => p && (PLANS[p] || PLANOS_SOB_MEDIDA[p])) || 'STARTER';
        setSession1(p => ({
          ...p,
          plan_type: plano,
          name: user.name || p.name,
          email: user.email || p.email,
          company_name: user.company_name || p.company_name,
        }));
        setTesteDisponivel(data.teste_disponivel !== false);
        setRetomando(true);
        setActiveStep(data.kyb_completo ? 2 : 1);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setTempToken('');
        }
      } finally {
        if (vivo) setCarregandoRetomada(false);
      }
    })();
    return () => { vivo = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Pixel na ativação (out/26) ────────────────────────────────────────
  // O Stripe só manda pra cá (?step=3&session_id=…) depois que o cartão foi
  // aceito: é o momento em que a pessoa vira cliente. Antes o
  // CompleteRegistration saía na criação da conta (sem cartão) e nada
  // avisava a Meta que o teste tinha começado — o Purchase ficava no
  // /checkout/success, onde a assinatura nunca passa.
  //   - CompleteRegistration sempre, com o valor do plano;
  //   - StartTrial se entrou com os 7 dias grátis;
  //   - Purchase se não entrou (quem já teve assinatura paga na hora).
  // pending_checkout ausente (voltou sem ele): só o CompleteRegistration.
  // Uma vez por sessão do Stripe — voltar à mesma URL não conta de novo —,
  // e o eventID é o id da sessão, pra a Meta juntar com o mesmo evento se
  // um dia ele também sair pelo servidor (API de Conversões).
  useEffect(() => {
    const sessao = params.get('session_id');
    if (urlStep !== 3 || !tempToken || !sessao) return;
    try {
      if (localStorage.getItem('pixel_ativacao') === sessao) return;
      localStorage.setItem('pixel_ativacao', sessao);
    } catch { return; }
    const plano = session1.plan_type;
    const dados = { value: getPlanValueBRL(plano), currency: 'BRL', content_name: plano };
    const opcoes = { eventID: sessao };
    trackFbqEvent('CompleteRegistration', { ...dados, status: true }, opcoes);
    if (voltouComTeste === true) trackFbqEvent('StartTrial', { ...dados, predicted_ltv: dados.value }, opcoes);
    if (voltouComTeste === false) trackFbqEvent('Purchase', { ...dados, content_type: 'subscription' }, opcoes);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Limpa o ?step da URL depois de usar — evita F5 voltar pro Step 4
  // sem ter feito o checkout antes.
  useEffect(() => {
    if (urlStep === 3 && activeStep === 3) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep]);

  // ─── Handlers do contrato (inalterados) ────────────────────────────────
  const handleContratoScroll = (e) => {
    const el = e.target;
    const scrolled = el.scrollTop + el.clientHeight;
    const total = el.scrollHeight;
    const progress = Math.min(100, Math.round((scrolled / total) * 100));
    setScrollProgress(progress);
    if (scrolled >= total - 10) setScrolledToEnd(true);
  };
  const handleAbrirContrato = () => {
    setContratoOpen(true);
    setScrolledToEnd(false);
    setScrollProgress(0);
  };
  const handleAceitarContrato = () => {
    setSession2(p => ({ ...p, terms_accepted: true }));
    setContratoOpen(false);
  };

  // ─── Change handlers (inalterados) ─────────────────────────────────────
  const handleS1Change = (e) => setSession1(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleS2Change = (e) => setSession2(p => ({ ...p, [e.target.name]: e.target.value }));

  // ─── Autofill CNPJ via Receita Federal (inalterado) ────────────────────
  const fetchCNPJ = useCallback(async (cnpj) => {
    const digits = cnpj.replace(/\D/g, '');
    if (digits.length !== 14) return;
    setCnpjLoading(true);
    try {
      const res = await axios.get(`${API_URL}/billing/cnpj/${digits}`, {
        headers: { Authorization: `Bearer ${tempToken}` }
      });
      const data = res.data;
      setSession2(p => ({ ...p, razao_social: data.razao_social || p.razao_social }));
    } catch { /* silencioso — usuário preenche manualmente */ }
    finally { setCnpjLoading(false); }
  }, [tempToken]);

  useEffect(() => {
    if (session2.cnpj.replace(/\D/g, '').length === 14) {
      fetchCNPJ(session2.cnpj);
    }
  }, [session2.cnpj, fetchCNPJ]);

  // ─── SESSÃO 1 — Registro inicial (inalterado) ──────────────────────────
  const handleSubmitSession1 = async () => {
    setError('');
    const { name, email, company_name, password, confirmPassword, plan_type } = session1;
    if (!name || !email || !company_name || !password) {
      setError('Preencha todos os campos obrigatórios.');
      return;
    }
    if (password !== confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }
    // 8, igual às Configurações e à recuperação de senha (out/26).
    if (password.length < 8) {
      setError('A senha deve ter no mínimo 8 caracteres.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/auth/register`, {
        name, email, company_name, password, plan: plan_type
      });
      const { access_token } = res.data;
      setTempToken(access_token);
      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      // Meta Pixel: o CompleteRegistration saiu daqui (out/26). Conta criada
      // sem cartão não é cliente — o evento vai na volta do Stripe, com o
      // teste ativado (ver "Pixel na ativação" abaixo).
      setCodigoEnviadoPara(email.trim());
      setActiveStep(1);
    } catch (err) {
      const detalhe = err.response?.data?.detail;
      // Conta já criada antes (cadastro abandonado): o caminho é entrar e
      // continuar de onde parou — o painel leva de volta ao cadastro.
      setError(detalhe === 'E-mail já cadastrado'
        ? 'Este e-mail já tem conta na Cetto. Entre com ele para continuar o cadastro de onde parou.'
        : detalhe || 'Erro ao criar conta. Tente novamente.');
    } finally { setLoading(false); }
  };

  // ─── SESSÃO 2 — KYB enxuto (inalterado) ────────────────────────────────
  const handleSubmitSession2 = async () => {
    setError('');
    const { razao_social, cnpj, website, terms_accepted } = session2;
    if (!razao_social || !cnpj || !website) {
      setError('Preencha CNPJ, Razão Social e Website para continuar.');
      return;
    }
    if (!terms_accepted) {
      setError('Você precisa aceitar os termos para continuar.');
      return;
    }
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${tempToken}` };
      await axios.post(`${API_URL}/billing/kyb`, { razao_social, cnpj, website }, { headers });
      const docUploads = [
        { file: session2.doc_cartao_cnpj, type: 'cartao_cnpj' },
        { file: session2.doc_contrato_social, type: 'contrato_social' },
        { file: session2.doc_comprovante_endereco, type: 'comprovante_endereco' },
      ];
      for (const doc of docUploads) {
        if (doc.file) {
          const formData = new FormData();
          formData.append('file', doc.file);
          await axios.post(`${API_URL}/billing/kyb/upload/${doc.type}`, formData, {
            headers: { ...headers, 'Content-Type': 'multipart/form-data' }
          });
        }
      }
      setActiveStep(2);
    } catch (err) {
      // Trata 3 formatos:
      //   1. detail string simples (HTTPException do FastAPI)
      //   2. detail array de validation errors (Pydantic 422)
      //   3. sem response (rede/CORS/backend down)
      let msg = 'Erro ao salvar dados empresariais.';
      const d = err.response?.data?.detail;
      if (typeof d === 'string') {
        msg = d;
      } else if (Array.isArray(d) && d.length) {
        const first = d[0];
        const field = Array.isArray(first?.loc) ? first.loc.slice(-1)[0] : '';
        msg = `Campo "${field}": ${first?.msg || 'inválido'}`;
      } else if (!err.response) {
        msg = 'Falha de conexão. Verifique a internet e tente novamente.';
      }
      setError(msg);
    } finally { setLoading(false); }
  };

  // ─── SESSÃO 3 — Stripe Checkout (inalterado em lógica;
  //                redirect mudou pra /register?step=3 no backend) ─────────
  const handleStartCheckout = async () => {
    setError('');
    setLoading(true);
    try {
      // Os 7 dias grátis são decididos no servidor (out/26): toda primeira
      // assinatura da empresa ganha, quem já teve não. A resposta diz se
      // entrou (`trial`), pro passo 4 falar a coisa certa.
      // Cookies do Pixel e a página vão junto pra API de Conversões (out/26):
      // é com eles que a Meta liga o evento do servidor ao anúncio.
      const res = await axios.post(
        `${API_URL}/billing/create-checkout-session`,
        {
          plan_type: session1.plan_type,
          ...dadosDeAtribuicao(),
          pagina: `${window.location.origin}${window.location.pathname}`,
        },
        { headers: { Authorization: `Bearer ${tempToken}` } }
      );

      // Meta Pixel: fire InitiateCheckout com valor real ANTES do redirect
      // pro Stripe. Salva contexto no localStorage pra volta do Stripe
      // saber o plano, o valor e se entrou com teste (o passo 4 e o Pixel
      // na ativação leem daqui, sem round-trip à /billing/subscription-status).
      const value = getPlanValueBRL(session1.plan_type);
      trackFbqEvent('InitiateCheckout', {
        value,
        currency: 'BRL',
        content_name: session1.plan_type,
      });
      localStorage.setItem('pending_checkout', JSON.stringify({
        plan_type: session1.plan_type,
        value,
        trial: !!res.data.trial,
        started_at: Date.now(),
      }));

      window.location.href = res.data.url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao iniciar pagamento. Tente novamente.');
      setLoading(false);
    }
  };

  // ─── Handlers do Step 4 ────────────────────────────────────────────────
  const handleEmbeddedSignupSuccess = (device) => {
    // Pequeno delay pra mostrar a tela de "Conectado" antes do redirect.
    setTimeout(() => navigate('/dashboard'), 1500);
  };
  const handleSkipWhatsApp = () => {
    // Cliente escolheu conectar depois — vai pro dashboard e a TopBar
    // vai mostrar a CTA "Conectar WhatsApp" (próxima fase).
    navigate('/dashboard');
  };

  // Plano sob medida (PLUS) aparece sozinho; o resto escolhe entre os públicos.
  const planoSobMedida = PLANOS_SOB_MEDIDA[session1.plan_type];
  const planosNaTela = planoSobMedida ? { [session1.plan_type]: planoSobMedida } : PLANS;
  const chavePlano = planosNaTela[session1.plan_type] ? session1.plan_type : 'STARTER';
  const escolherPlano = (k) => setSession1(p => ({ ...p, plan_type: k }));
  // Etapa 3: "Trocar para" entre Starter e Business (sob medida não troca).
  const outroPlano = planoSobMedida ? null : (chavePlano === 'STARTER' ? 'BUSINESS' : 'STARTER');

  // ═══════════════════════════════════════════════════════════════
  // MODAL DO CONTRATO — preservado da v1, só repintado pra paleta nova.
  // O mesmo nos dois visuais (cadastro comum e Black Friday).
  // ═══════════════════════════════════════════════════════════════
  const modalContrato = (
      <Dialog
        open={contratoOpen}
        onClose={() => setContratoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: C.bg2, border: `1px solid ${C.bdr}`,
            borderRadius: '10px', color: C.text, fontFamily: FONT_TXT,
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${C.bdr}`, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography sx={{ fontFamily: FONT_TXT, fontWeight: 700, fontSize: '1.05rem', color: '#fff' }}>
                Contrato de Prestação de Serviços
              </Typography>
              <Typography sx={{ fontFamily: FONT_TXT, fontSize: '0.68rem', color: C.text3 }}>
                Leia o contrato na íntegra antes de aceitar
              </Typography>
            </Box>
            <IconButton onClick={() => setContratoOpen(false)} sx={{ color: C.text3, '&:hover': { color: '#fff' } }}>
              ✕
            </IconButton>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography sx={{ fontSize: '0.65rem', color: C.text3, fontFamily: FONT_TXT }}>
                Progresso de leitura
              </Typography>
              <Typography sx={{ fontSize: '0.65rem', color: scrollProgress === 100 ? C.green : C.text3, fontFamily: FONT_NUM, fontWeight: 600 }}>
                {scrollProgress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={scrollProgress}
              sx={{
                height: 4, borderRadius: 2,
                bgcolor: C.bg4,
                '& .MuiLinearProgress-bar': {
                  bgcolor: scrollProgress === 100 ? C.green : C.blue,
                  borderRadius: 2, transition: 'background-color 0.3s ease',
                }
              }}
            />
          </Box>
        </DialogTitle>

        <DialogContent
          onScroll={handleContratoScroll}
          ref={contratoRef}
          sx={{
            py: 3, px: 4, maxHeight: '55vh', overflowY: 'auto',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.05)' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.15)', borderRadius: 3 },
          }}
        >
          {CONTRATO_CETTO.split('\n').map((linha, i) => (
            <Typography
              key={i}
              sx={{
                fontFamily: FONT_TXT,
                color: linha.startsWith('CLÁUSULA') ? '#fff' : C.text2,
                fontWeight: linha.startsWith('CLÁUSULA') ? 700 : 400,
                mb: linha === '' ? 1.5 : 0.5,
                lineHeight: 1.7,
                fontSize: linha.startsWith('CLÁUSULA') ? '0.85rem' : '0.78rem',
              }}
            >
              {linha || ' '}
            </Typography>
          ))}
        </DialogContent>

        <DialogActions sx={{ borderTop: `1px solid ${C.bdr}`, p: 3, gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography sx={{
              fontSize: '0.68rem', fontFamily: FONT_TXT,
              color: scrolledToEnd ? C.green : C.text3,
            }}>
              {scrolledToEnd ? '✓ Você leu o contrato na íntegra' : 'Role até o final para habilitar o aceite'}
            </Typography>
          </Box>
          <Button
            onClick={() => setContratoOpen(false)}
            sx={{ color: C.text3, textTransform: 'none', fontFamily: FONT_TXT }}
          >
            Fechar
          </Button>
          <Button
            variant="contained"
            onClick={handleAceitarContrato}
            disabled={!scrolledToEnd}
            sx={{
              bgcolor: scrolledToEnd ? C.green : C.bg4,
              color: scrolledToEnd ? C.bg : C.text3,
              fontFamily: FONT_TXT,
              fontWeight: 700, textTransform: 'none', px: 3, borderRadius: '7px',
              '&:hover': { bgcolor: scrolledToEnd ? '#00f090' : C.bg4 },
              '&.Mui-disabled': { bgcolor: C.bg4, color: C.text3 },
            }}
          >
            Li e Aceito o Contrato
          </Button>
        </DialogActions>
      </Dialog>
  );

  // Tudo que as duas telas recebem do cadastro.
  const doCadastro = {
    activeStep, setActiveStep, retomando, carregandoRetomada,
    error, setError, loading, cnpjLoading,
    session1, handleS1Change, session2, handleS2Change, setSession2,
    handleSubmitSession1, handleSubmitSession2, handleStartCheckout,
    handleAbrirContrato, testeDisponivel, codigoEnviadoPara, voltouComTeste,
    onEsqueciSenha: () => navigate('/recuperar-senha', { state: { email: session1.email.trim() } }),
    onEmbeddedSignupSuccess: handleEmbeddedSignupSuccess,
    onSkipWhatsApp: handleSkipWhatsApp,
  };

  return (
    <>
      {/* Black Friday: o PLUS no visual da oferta, com a mesma lógica. */}
      {CAMPANHA_LIGADA && session1.plan_type === 'PLUS' ? (
        <CadastroPlus {...doCadastro} plano={PLANOS_SOB_MEDIDA.PLUS} />
      ) : (
        <CadastroComum
          {...doCadastro}
          planos={planosNaTela} chavePlano={chavePlano} escolherPlano={escolherPlano}
          alternativa={outroPlano ? PLANS[outroPlano] : null}
          trocarPlano={() => outroPlano && escolherPlano(outroPlano)}
        />
      )}
      {modalContrato}
    </>
  );
}
